import { useState } from 'react'
import {
  ApiError, api, fmtTime, retireCompute, setRetention, setService,
  type CameraInfo, type DesiredState, type Me,
} from '../api'
import { useT } from '../lang'
import { invalidate, useApi } from '../useApi'
import { Link } from '../router'
import { PageHead } from '../components/Shell'
import { Empty, ErrorBox, Field, Rows, StateBadge } from '../components/bits'
import { Move } from '../components/Move'
import { NodeCamera } from '../components/NodeCamera'

interface Compute {
  compute_id: string
  hw_serial: string
  display_name: string | null
  model: string | null
  full_scale_dbv: number | null
  full_scale_estimated: boolean
  sw_version: string | null
  enrolled_at: string
  revoked_at: string | null
  missions: {
    mission_id: string
    site_id: string | null
    site_name: string | null
    state: string
    started_at: string
    ended_at: string | null
    /** Set on a planned mission waiting for this node to turn up. */
    activate_after: string | null
  }[]
  /** The hydrophone in the water on this unit right now, through its active
   *  mission. Null when nothing is deployed on it. */
  microphone: {
    microphone_id: string
    type: string | null
    serial: string | null
    max_depth_m: number | null
  } | null
  /** The card, and what it can actually hold. Null until the node has
   *  reported its filesystem. */
  disk: {
    total_bytes: number
    used_bytes: number
    high_water_frac: number
    bytes_per_hour: number | null
    achievable_retention_hours: number | null
    retention_hours: number | null
    observed_utc: string
  } | null
  /** What was asked of the node's retention, and what it is actually running.
   *
   *  These differ when a push does not take effect, which revision_applied
   *  cannot detect: that only says the node fetched the revision. */
  retention: {
    asked_hours: number | null
    effective_hours: number | null
    observed_utc: string | null
    asked_at: string | null
    pending: boolean
    applied: boolean | null
  } | null
  /** The camera, as the node last reported it. Null on a node without one. */
  camera: CameraInfo | null
}

export function NodePage({ id, me }: { id: string; me: Me }) {
  const t = useT()
  const path = `/v1/computes/${encodeURIComponent(id)}`
  const q = useApi<Compute>(path)
  const c = q.data

  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')
  const [fsv, setFsv] = useState('')
  const [fsEst, setFsEst] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  // Retiring stops the node authenticating, so it asks first -- and asks in
  // the card rather than in a window.confirm(), which on a phone is a system
  // dialog with no room to say what the consequence actually is.
  const [confirming, setConfirming] = useState(false)

  if (q.cold) return <Rows n={6} />
  if (q.error) return <ErrorBox error={q.error} />
  if (!c) return null

  const startEdit = () => {
    setName(c.display_name ?? '')
    setFsv(c.full_scale_dbv?.toString() ?? '')
    setFsEst(c.full_scale_estimated)
    setEditing(true)
    setErr(null)
  }

  const save = async () => {
    setBusy(true)
    setErr(null)
    try {
      const body: Record<string, unknown> = { display_name: name || null }
      // Sent together, always. A value and its provenance that can be saved
      // apart will drift apart, and the half that goes missing is the one
      // saying not to trust the number.
      if (fsv !== '') {
        body.full_scale_dbv = Number(fsv)
        body.full_scale_estimated = fsEst
      }
      await api.patch(path, body)
      invalidate('/v1/computes')
      invalidate('/v1/fleet')
      setEditing(false)
      q.reload()
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail ?? e.code : 'Save failed.')
    } finally {
      setBusy(false)
    }
  }

  const active = c.missions.find((m) => m.state === 'active')
  const retired = !!c.revoked_at

  const setRetired = async (next: boolean) => {
    setBusy(true)
    setErr(null)
    try {
      await retireCompute(c.compute_id, next)
      invalidate('/v1/computes')
      invalidate('/v1/fleet')
      setConfirming(false)
      q.reload()
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail ?? e.code : 'Could not change this.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHead
        title={c.display_name || c.compute_id}
        lead={<span className="mono">{c.compute_id}</span>}
        cta={
          active && (
            <Link className="btn primary" to={`/missions/${encodeURIComponent(active.mission_id)}`}>
              Open current deployment
            </Link>
          )
        }
      />

      <div className="stack">
        <div className="card">
          <dl className="dl">
            <dt>Hardware serial</dt>
            {/* R2.5: this is the permanent binding that stops a cloned SD card
                putting two live nodes on one identity. Shown because someone
                holding the board needs to check it against the label. */}
            <dd className="mono">{c.hw_serial}</dd>
            <dt>{t('Model')}</dt>
            <dd>{c.model ?? <span className="faint">—</span>}</dd>
            <dt>Software</dt>
            <dd className="mono">{c.sw_version ?? '—'}</dd>
            <dt>Enrolled</dt>
            <dd className="num faint">{fmtTime(c.enrolled_at)}</dd>
            <dt>Hydrophone</dt>
            <dd>
              {c.microphone ? (
                <>
                  <Link to={`/calibration/${encodeURIComponent(c.microphone.microphone_id)}`}>
                    {c.microphone.type ?? 'unknown model'}
                  </Link>
                  {c.microphone.serial && (
                    <div className="mono faint">{c.microphone.serial}</div>
                  )}
                </>
              ) : <span className="faint">none in the water</span>}
            </dd>
            <dt>Full scale</dt>
            <dd className="num">
              {c.full_scale_dbv != null ? (
                <>
                  {c.full_scale_dbv} dBV
                  {/* The provenance travels with the number wherever it is
                      printed. A datasheet figure that has lost this reads as
                      a bench measurement, which is the whole reason the
                      column exists. */}
                  {c.full_scale_estimated && (
                    <span className="badge" style={{ marginLeft: 8 }}>estimated</span>
                  )}
                </>
              ) : <span className="faint">not measured</span>}
            </dd>
          </dl>

          {me.can_write && !editing && (
            <button className="btn sm" style={{ marginTop: 16 }} onClick={startEdit}>
              Edit node
            </button>
          )}

          {editing && (
            <div className="stack" style={{ marginTop: 16, gap: 14 }}>
              <Field label="Display name" hint="What technicians read on the fleet list instead of the identifier.">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Bergen Nord Pi" />
              </Field>
              <Field
                label="Full scale (dBV)"
                hint="What digital full scale is in volts, with the analogue gain backed out. One of the three terms needed before a level can be absolute. A Codec Zero sits near −3 dBV."
              >
                <input type="number" step="0.1" value={fsv} onChange={(e) => setFsv(e.target.value)} />
              </Field>
              {/* Asked immediately under the value, and unavoidable: the
                  Codec Zero's own datasheet route carries 4.4 dB of spread
                  from one unverified supply rail, and the schematic that
                  would settle it is unpublished. A figure from there is a
                  usable interim and is not a measurement. */}
              <label className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
                <input type="checkbox" checked={fsEst}
                  onChange={(e) => setFsEst(e.target.checked)} />
                <span>
                  <strong style={{ fontSize: 14 }}>Estimated, not measured</strong>
                  <span className="faint" style={{ display: 'block', fontSize: 13 }}>
                    Tick this if the figure came off a datasheet rather than a
                    meter on this board. Every level built from it is then
                    reported as “dB re 1 µPa (estimated)”.
                  </span>
                </span>
              </label>
              {err && <div className="notice bad">{err}</div>}
              <div className="row">
                {/* Save outranks Cancel by being a solid button next to a
                    ghost one, which is hierarchy enough. The accent stays with
                    the page's one call to action. */}
                <button className="btn sm" disabled={busy} onClick={() => void save()}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
                <button className="btn ghost sm" onClick={() => setEditing(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>

        {me.can_write && !retired && (
          <Move compute={c.compute_id} onSaved={() => q.reload()} />
        )}

        {me.can_write && (
          <div className="card">
            <div className="card-head" style={{ paddingLeft: 0, paddingRight: 0 }}>
              <h2>{retired ? 'Out of service' : 'In service'}</h2>
              {retired && <span className="badge">retired {fmtTime(c.revoked_at)}</span>}
            </div>

            {retired ? (
              <>
                <p className="faint" style={{ marginTop: 4 }}>
                  Hidden from the hydrophone list, and the unit cannot upload.
                  Nothing was deleted — its recordings, deployments and
                  calibration history are all still here.
                </p>
                <button className="btn sm" disabled={busy}
                  onClick={() => void setRetired(false)}>
                  {busy ? 'Working…' : 'Put back in service'}
                </button>
              </>
            ) : !confirming ? (
              <>
                <p className="faint" style={{ marginTop: 4 }}>
                  Retire a unit that was only ever a test, or one that has come
                  out of the water for good. It disappears from the hydrophone
                  list without anything being deleted.
                </p>
                <button className="btn sm" onClick={() => { setErr(null); setConfirming(true) }}>
                  Retire this unit
                </button>
              </>
            ) : (
              <div className="stack" style={{ marginTop: 8, gap: 12 }}>
                <div className="notice">
                  <strong>Retire {c.display_name || c.compute_id}?</strong>
                  <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                    <li>It leaves the hydrophone list.</li>
                    <li>
                      The unit can no longer upload — retiring is what revokes
                      its access, so a node still in the field goes quiet.
                    </li>
                    {active && (
                      <li>
                        Its current deployment{' '}
                        <span className="mono">{active.mission_id}</span> is
                        closed and archived.
                      </li>
                    )}
                    <li>
                      Nothing is deleted, and putting it back is one click.
                    </li>
                  </ul>
                </div>
                <div className="row">
                  <button className="btn danger sm" disabled={busy}
                    onClick={() => void setRetired(true)}>
                    {busy ? 'Working…' : 'Retire it'}
                  </button>
                  <button className="btn ghost sm" onClick={() => setConfirming(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {err && <div className="notice bad" style={{ marginTop: 12 }}>{err}</div>}
          </div>
        )}

        <NodeServices computeId={c.compute_id} me={me} disk={c.disk}
                       retention={c.retention} />

        {/* Staff only, and only while deployed: live and recorded video are
            addressed by mission, like everything else that leaves the node. */}
        {me.can_write && c.camera && active && (
          <NodeCamera computeId={c.compute_id} missionId={active.mission_id}
                      me={me} camera={c.camera} />
        )}

        <div className="card flush">
          <div className="card-head"><h2>Deployments</h2></div>
          {c.missions.length === 0 ? (
            <Empty title="Never deployed" />
          ) : (
            <table className="tbl">
              <thead>
                <tr><th>Mission</th><th>Site</th><th>State</th><th>From</th></tr>
              </thead>
              <tbody>
                {c.missions.map((m) => (
                  <tr key={m.mission_id}>
                    <td data-l="Mission">
                      <Link className="rowlink" to={`/missions/${encodeURIComponent(m.mission_id)}`}>
                        {m.mission_id}
                      </Link>
                    </td>
                    <td data-l="Site" className="faint">
                      {m.site_id ? (
                        <Link to={`/sites/${encodeURIComponent(m.site_id)}`}>{m.site_id}</Link>
                      ) : '—'}
                    </td>
                    <td data-l="State"><StateBadge state={m.state} /></td>
                    <td data-l="From" className="num faint">{fmtTime(m.started_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  )
}


/** What this node has been asked to run.
 *
 *  Desired state, not commands (ground rule 6): these write what the node
 *  should be doing and the node's reconciler converges on it the next time it
 *  polls, which is seconds away but is not instant and is not acknowledged.
 *
 *  This exists because there was no way to set them. `capture` and `uplink` are
 *  seeded at enrolment, and a node enrolled before that code shipped -- or one
 *  whose desired state was first written by pressing Listen -- ended up with
 *  `{"live": "running"}` and nothing else. It then ran live with no capture to
 *  produce audio and no uplink to ship it, recorded happily to its own card,
 *  and uploaded nothing. Nothing in the interface could get it out of that
 *  state, and the symptom appeared on the Pi as a busy audio device.
 *
 *  `live` is deliberately absent here. It is on demand by design (R7.1), it
 *  costs a segment of uplink a second, and it belongs to the Listen tab where
 *  somebody is actually listening -- a toggle here would leave nodes streaming
 *  to nobody.
 */
function NodeServices({ computeId, me, disk, retention }: {
  computeId: string; me: Me; disk: Compute['disk']
  retention: Compute['retention']
}) {
  const t = useT()
  const path = `/v1/computes/${encodeURIComponent(computeId)}/desired-state`
  const q = useApi<DesiredState>(path)
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  /* What we have just asked for, shown immediately.
   *
   * The reload after a write is a round trip, and during it the button still
   * read "Start" for a service that was now running. Pressing it again -- the
   * reasonable thing to do when nothing appears to have happened -- turned it
   * straight back off. The audit trail showed exactly that: capture on at
   * 12:48:59 and off at 12:49:07, uplink on at :09 and off at :15. */
  const [pending, setPending] = useState<Record<string, boolean>>({})

  const services = q.data?.services ?? {}

  const toggle = async (name: string, on: boolean) => {
    setBusy(name)
    setErr(null)
    try {
      await setService(computeId, name, on ? 'running' : null)
      setPending((p) => ({ ...p, [name]: on }))
      invalidate(path)
      q.reload()
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail ?? e.code : `Could not change ${name}.`)
    } finally {
      setBusy(null)
    }
  }

  /* Presets rather than a free number field. The useful values are bounded by
   * the card, and a box that accepts 0.1 invites a value the node will refuse
   * -- its floor exists because a smaller one makes the janitor wipe the
   * spool on its next run. `null` hands the decision back to node.toml. */
  const ALL_RETENTIONS: { h: number | null; label: string }[] = [
    { h: 24, label: '1 day' },
    { h: 72, label: '3 days' },
    { h: 168, label: '7 days' },
    { h: null, label: 'Node decides' },
  ]

  /* Offered only if the disk can hold it.
   *
   * This screen offered seven days to a board measured at about 68 hours: 58
   * GiB of card, 8.3 GiB of it not audio, and a 70% high-water mark that binds
   * long before the age cap does. `max_audio_hours` is a request; the disk is
   * the answer, and past the crossover raising retention changes nothing except
   * what this page claims.
   *
   * The figure comes from the node's own filesystem report, so a bigger card
   * widens the list without anything here changing. Until a node has reported
   * one, every option stays -- an unmeasured node is not a small one, and
   * hiding choices on no evidence would be its own kind of wrong. */
  const canHold = disk?.achievable_retention_hours ?? null
  const RETENTIONS = canHold === null
    ? ALL_RETENTIONS
    : ALL_RETENTIONS.filter((r) => r.h === null || r.h <= canHold)

  // Parsed back out of the overlay the receiver generated, so this shows what
  // the node was actually told rather than what this browser last sent.
  const currentHours = (() => {
    const m = /max_audio_hours\s*=\s*([\d.]+)/.exec(q.data?.config_overlay ?? '')
    return m ? Number(m[1]) : null
  })()
  /* What the node is RUNNING, not what it was asked for.
   *
   * Those are different, and a screen that shows the request as though it were
   * the state is how the first real push went unnoticed: it asked for 48 h, the
   * node kept 24 h because its uplink unit could not write the overlay, and
   * every signal read healthy because revision_applied only says the node
   * fetched the revision. */
  const running = retention?.effective_hours ?? currentHours
  const fmtH = (h: number) => h >= 48 ? `${Math.round(h / 24)} days` : `${h} hours`
  const retentionLabel = running === null || running === undefined
    ? 'as configured on the node'
    : fmtH(running)

  const changeRetention = async (hours: number | null) => {
    setBusy('retention')
    setErr(null)
    try {
      await setRetention(computeId, hours)
      invalidate(path)
      q.reload()
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail ?? e.code : 'Could not change retention.')
    } finally {
      setBusy(null)
    }
  }

  const ROWS: { name: string; label: string; hint: string }[] = [
    { name: 'capture', label: 'Record',
      hint: 'Writes audio to the node\'s own card. Everything else depends on this: with it off there is nothing to analyse, ship or listen to.' },
    { name: 'uplink', label: 'Upload',
      hint: 'Ships roll-ups and anything the tier permits to object storage. With it off the node keeps recording locally and nothing reaches here.' },
  ]

  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 4 }}>
        <h2 className="display" style={{ fontSize: 17 }}>{t('Services')}</h2>
        {q.data?.mission_id
          ? <span className="badge">{q.data.tier ?? 'no tier'}</span>
          : <span className="badge alert">no mission</span>}
      </div>
      <p className="faint" style={{ fontSize: 12, margin: '0 0 14px' }}>
        What this node is asked to run. It picks changes up the next time it
        checks in — there is no command channel, so nothing here is instant and
        nothing confirms it landed except the node going quiet or starting to
        send.
      </p>

      {q.cold ? <Rows n={2} /> : (
        <dl className="dl">
          {ROWS.map((r) => {
            // The server's answer once it arrives, and what we asked for
            // until then.
            const on = pending[r.name] ?? (services[r.name] === 'running')
            return (
              <>
                <dt key={`${r.name}-t`}>{r.label}</dt>
                <dd key={`${r.name}-d`}>
                  <div className="row" style={{ gap: 10 }}>
                    <span className={`badge${on ? ' on' : ' off'}`}>
                      {on ? 'running' : 'stopped'}
                    </span>
                    {me.can_write && (
                      <button className={`btn sm${on ? ' ghost' : ''}`}
                              disabled={busy === r.name}
                              onClick={() => void toggle(r.name, !on)}>
                        {busy === r.name ? 'Saving…' : on ? `Stop ${r.label.toLowerCase()}` : `Start ${r.label.toLowerCase()}`}
                      </button>
                    )}
                  </div>
                  <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>{r.hint}</div>
                </dd>
              </>
            )
          })}
          <dt>Live</dt>
          <dd>
            <span className={`badge${services.live === 'running' ? ' on' : ' off'}`}>
              {services.live === 'running' ? 'streaming' : 'off'}
            </span>
            <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>
              On demand, from the Listen tab. Not a setting: it costs a segment
              of uplink every second, so it is never left on.
            </div>
          </dd>

          <dt>Keeps audio</dt>
          <dd>
            <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
              <span className={`badge${retention?.applied === false ? ' alert' : ''}`}>
                {retentionLabel}
              </span>
              {me.can_write && RETENTIONS.map((r) => (
                <button key={String(r.h)} className="btn sm ghost"
                  disabled={busy === 'retention' || r.h === currentHours}
                  onClick={() => void changeRetention(r.h)}>
                  {r.label}
                </button>
              ))}
            </div>
            {retention?.applied === false && (
              <div className="notice bad" style={{ margin: '8px 0' }}>
                Asked for {fmtH(retention.asked_hours ?? 0)}, but the node
                reported {fmtH(retention.effective_hours ?? 0)} at{' '}
                {fmtTime(retention.observed_utc)} — after the change was made.
                The instruction reached it and did not take effect, so the card
                still holds what it did before.
              </div>
            )}
            {retention?.pending && retention.asked_hours !== null && (
              <div className="notice" style={{ margin: '8px 0' }}>
                Asked for {fmtH(retention.asked_hours)}. Waiting for the node to
                report back — it has not checked in since the change.
              </div>
            )}
            <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>
              How far back the card reaches. This is the ceiling on what can
              ever be fetched: asking for a range only moves it to the back of
              the queue for deletion, it cannot postpone it — so audio older
              than this is gone rather than slow.
              {disk?.achievable_retention_hours != null ? (
                <>
                  {' '}This card measures {Math.round((disk.bytes_per_hour ?? 0) / 1e6)} MB
                  an hour and can hold about{' '}
                  <strong>{Math.round(disk.achievable_retention_hours)} hours</strong>{' '}
                  ({(disk.achievable_retention_hours / 24).toFixed(1)} days).
                  Past that the {Math.round((disk.high_water_frac ?? 0.7) * 100)}%
                  {' '}disk limit clears audio on every run whatever this says,
                  so longer options are not offered.
                </>
              ) : (
                <>
                  {' '}This node has not reported its card yet, so every option
                  is shown. Roughly 490 MB an hour on the fleet so far.
                </>
              )}
            </div>
          </dd>
        </dl>
      )}

      {err && <div className="notice bad" style={{ marginTop: 12 }}>{err}</div>}
    </div>
  )
}
